import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { getLink, initVideoUpload, LinkPublic } from "../api";
import Teleprompter from "../components/Teleprompter";
import { distributeTiming, parseScript } from "../utils";

type Phase =
  | "loading"
  | "briefing"       // briefing autoplays fullscreen with pause
  | "briefing_end"   // briefing finished → "Ready to record" CTA
  | "ready"          // camera acquired, self-view + teleprompter, waiting for Record
  | "countdown"
  | "recording"
  | "preview"
  | "uploading"
  | "done"
  | "error";

export default function Record() {
  const { linkId } = useParams<{ linkId: string }>();
  const [search] = useSearchParams();
  const token = search.get("token") ?? undefined;

  const [link, setLink] = useState<LinkPublic | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [briefingMuted, setBriefingMuted] = useState(true);
  const [briefingPlaying, setBriefingPlaying] = useState(true);
  // CSS-only zoom for the camera self-view (iOS Safari does not expose
  // MediaTrack zoom). Two discrete steps: 1× (full captured frame) and 2×.
  const [cameraZoom, setCameraZoom] = useState<1 | 2>(1);

  const briefingVideoRef = useRef<HTMLVideoElement>(null);
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const blobRef = useRef<Blob | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // screen.orientation.angle captured the instant MediaRecorder starts.
  // iPhone Safari encodes the camera sensor in landscape no matter how the
  // phone is held + drops orientation metadata in webm, so the transcoder
  // needs this hint to apply the right ffmpeg transpose.
  const orientationAngleRef = useRef<number | null>(null);
  const elapsedTimerRef = useRef<number>();
  const stopTimeoutRef = useRef<number>();

  // Fetch link metadata
  useEffect(() => {
    if (!linkId) return;
    getLink(linkId, token)
      .then((l) => {
        setLink(l);
        setPhase(l.briefing_video_url ? "briefing" : "briefing_end");
      })
      .catch((e) => {
        setError(e.message || "Link unavailable");
        setPhase("error");
      });
  }, [linkId, token]);

  // Attach camera stream to the video element when in "ready" phase
  useEffect(() => {
    if (
      (phase === "ready" ||
        phase === "countdown" ||
        phase === "recording") &&
      streamRef.current &&
      cameraVideoRef.current &&
      cameraVideoRef.current.srcObject !== streamRef.current
    ) {
      cameraVideoRef.current.srcObject = streamRef.current;
      cameraVideoRef.current.muted = true;
      cameraVideoRef.current.play().catch(() => {});
    }
  }, [phase]);

  // Attach recorded blob to preview video when entering preview phase
  useEffect(() => {
    if (phase === "preview" && blobRef.current && previewVideoRef.current) {
      previewVideoRef.current.src = URL.createObjectURL(blobRef.current);
      previewVideoRef.current.muted = false;
      previewVideoRef.current.controls = true;
    }
  }, [phase]);

  const acquireCamera = useCallback(async () => {
    // Native sensor aspect (typically 4:3 on iPhone front cam). Forcing a
    // 9:16 portrait constraint asked the browser to side-crop the sensor,
    // which felt like a heavy zoom. We now let the camera return its full
    // field of view; display crops via object-cover.
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user" },
      audio: true,
    });
    streamRef.current = stream;
  }, []);

  const readyToRecord = useCallback(async () => {
    try {
      await acquireCamera();
      setPhase("ready");
    } catch (e: any) {
      setError(
        e.name === "NotAllowedError"
          ? "Accès caméra/micro refusé. Autorise-les dans le navigateur puis recharge."
          : e.message || "Impossible d'accéder à la caméra"
      );
      setPhase("error");
    }
  }, [acquireCamera]);

  const handleBriefingEnded = useCallback(() => {
    setPhase("briefing_end");
    setBriefingPlaying(false);
  }, []);

  const skipBriefing = useCallback(() => {
    briefingVideoRef.current?.pause();
    setPhase("briefing_end");
  }, []);

  const toggleBriefingPlay = useCallback(() => {
    const v = briefingVideoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setBriefingPlaying(true);
    } else {
      v.pause();
      setBriefingPlaying(false);
    }
  }, []);

  const unmuteBriefing = useCallback(() => {
    if (briefingVideoRef.current) {
      briefingVideoRef.current.muted = false;
      setBriefingMuted(false);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (stopTimeoutRef.current) {
      window.clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = undefined;
    }
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === "recording") mr.stop();
    if (elapsedTimerRef.current) window.clearInterval(elapsedTimerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = streamRef.current;
      if (!stream) throw new Error("Caméra non initialisée");

      setPhase("countdown");
      for (let i = 3; i >= 1; i--) {
        setCountdown(i);
        await new Promise((r) => setTimeout(r, 1000));
      }

      const mimeType = MediaRecorder.isTypeSupported(
        "video/webm;codecs=vp9,opus"
      )
        ? "video/webm;codecs=vp9,opus"
        : "video/webm";
      const mr = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 2_500_000,
      });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        blobRef.current = blob;
        setPhase("preview");
      };
      mr.start(250);
      mediaRecorderRef.current = mr;
      // Capture the phone's orientation NOW; the user might rotate before
      // we get to submit and we'd lose the original record-time pose.
      orientationAngleRef.current =
        typeof window !== "undefined" && window.screen?.orientation
          ? window.screen.orientation.angle
          : null;
      setPhase("recording");
      setElapsed(0);

      const maxMs = (link?.max_duration_sec ?? 120) * 1000;
      // Keep recording 500ms past the visible end-of-prompter to avoid clipping
      // the last word — the elapsed counter caps at maxSec but the MediaRecorder
      // is stopped slightly later.
      const STOP_BUFFER_MS = 500;
      elapsedTimerRef.current = window.setInterval(() => {
        setElapsed((e) => {
          const next = e + 1;
          if (next * 1000 >= maxMs && !stopTimeoutRef.current) {
            if (elapsedTimerRef.current) {
              window.clearInterval(elapsedTimerRef.current);
              elapsedTimerRef.current = undefined;
            }
            stopTimeoutRef.current = window.setTimeout(stopRecording, STOP_BUFFER_MS);
          }
          return next;
        });
      }, 1000);
    } catch (e: any) {
      setError(e.message || "Could not start recording");
      setPhase("error");
    }
  }, [link, stopRecording]);

  const retake = useCallback(async () => {
    blobRef.current = null;
    chunksRef.current = [];
    setElapsed(0);
    try {
      await acquireCamera();
      setPhase("ready");
    } catch (e: any) {
      setError(e.message || "Could not re-acquire camera");
      setPhase("error");
    }
  }, [acquireCamera]);

  const submit = useCallback(async () => {
    if (!blobRef.current || !linkId) return;
    try {
      setPhase("uploading");
      const init = await initVideoUpload({
        link_id: linkId,
        token,
        mime_type: "video/webm",
        recorder_name: link?.recipient_name ?? undefined,
        recorder_orientation_angle:
          orientationAngleRef.current ?? undefined,
      });

      const putRes = await fetch(init.upload_url, {
        method: "PUT",
        headers: { "Content-Type": "video/webm" },
        body: blobRef.current,
      });
      if (!putRes.ok) throw new Error("Upload failed");
      setPhase("done");
    } catch (e: any) {
      setError(e.message || "Upload failed");
      setPhase("error");
    }
  }, [linkId, token, link]);

  // ── Render ──────────────────────────────────────────────────────
  const maxSec = link?.max_duration_sec ?? 120;
  const parsed = useMemo(() => parseScript(link?.text ?? ""), [link?.text]);
  const timing = useMemo(
    () => distributeTiming(parsed.segments, maxSec),
    [parsed.segments, maxSec],
  );

  if (phase === "loading") {
    return <Full>Chargement…</Full>;
  }

  if (phase === "error") {
    return (
      <Full>
        <div className="text-center space-y-3 max-w-md">
          <div className="text-red-400 text-lg">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-2 bg-ivory/10 text-ivory rounded-lg hover:bg-ivory/20"
          >
            Réessayer
          </button>
        </div>
      </Full>
    );
  }

  if (phase === "done") {
    return (
      <div className="fixed inset-0 bg-charcoal text-ivory flex flex-col items-center justify-center p-8">
        <img
          src="/chief/chief-7.png"
          alt=""
          className="w-48 h-48 md:w-64 md:h-64 object-contain mb-6"
          onError={(e) => {
            // Graceful fallback if PNG not copied yet
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <div className="text-center space-y-2 max-w-md">
          <h1 className="text-5xl md:text-6xl font-black leading-none">
            Nailed it.
          </h1>
          <p className="text-stone text-lg mt-3">
            Your video is on its way.
          </p>
          {link?.is_avatar_link && (
            <p className="text-stone/90 text-sm mt-4 leading-relaxed">
              Cette prise est destinée à un avatar : l’organisateur lancera la génération depuis
              son espace admin lorsque la vidéo sera prête.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Briefing phase — full-screen autoplay video
  if (phase === "briefing" && link?.briefing_video_url) {
    return (
      <div className="fixed inset-0 bg-black">
        <video
          ref={briefingVideoRef}
          src={link.briefing_video_url}
          autoPlay
          muted={briefingMuted}
          playsInline
          onEnded={handleBriefingEnded}
          onPlay={() => setBriefingPlaying(true)}
          onPause={() => setBriefingPlaying(false)}
          className="w-full h-full object-contain"
        />

        {/* Top-right: Skip */}
        <button
          onClick={skipBriefing}
          className="absolute top-4 right-4 px-4 py-2 bg-white/20 backdrop-blur-sm text-ivory rounded-lg hover:bg-white/30 transition text-sm font-medium"
        >
          Passer →
        </button>

        {/* Bottom controls */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3">
          <button
            onClick={toggleBriefingPlay}
            className="w-14 h-14 rounded-full bg-ivory text-charcoal flex items-center justify-center text-2xl hover:bg-white transition shadow-xl"
            aria-label={briefingPlaying ? "Pause" : "Play"}
          >
            {briefingPlaying ? "⏸" : "▶"}
          </button>
          {briefingMuted && (
            <button
              onClick={unmuteBriefing}
              className="h-14 px-5 rounded-full bg-lemonade text-charcoal font-semibold hover:brightness-95 transition shadow-xl text-sm"
            >
              🔊 Activer le son
            </button>
          )}
        </div>
      </div>
    );
  }

  // Transition — "Ready to record" CTA (LLYLL style: two-tone headline)
  if (phase === "briefing_end") {
    return (
      <div className="fixed inset-0 bg-charcoal text-ivory flex flex-col p-6 md:p-10">
        <div className="flex-1 flex flex-col justify-center max-w-2xl mx-auto w-full">
          {link?.recipient_name && (
            <p className="text-stone text-lg mb-6">Hey {link.recipient_name} 👋</p>
          )}
          <h1 className="leading-[1.05]">
            <span className="block text-5xl md:text-6xl font-black text-lemonade">
              Record a short
            </span>
            <span className="block text-5xl md:text-6xl font-black text-ivory">
              video of yourself.
            </span>
          </h1>
          <p className="mt-8 text-stone text-lg md:text-xl">
            {maxSec} seconds, face the camera, read the prompter.
          </p>
        </div>

        <button
          onClick={readyToRecord}
          className="w-full py-5 bg-lemonade text-black text-xl font-bold rounded-xl hover:brightness-95 transition shadow-2xl"
        >
          Hit the record
        </button>
        <p className="text-ash text-xs text-center mt-3">
          Your browser will ask for camera &amp; mic access.
        </p>
      </div>
    );
  }

  // Record phases — full-screen cam with teleprompter overlay at top
  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {/* Camera self-view (mirrored) — ready / countdown / recording */}
      {(phase === "ready" || phase === "countdown" || phase === "recording") && (
        <video
          ref={cameraVideoRef}
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            transform: `scaleX(-1) scale(${cameraZoom})`,
            transformOrigin: "center center",
            transition: "transform 120ms ease-out",
          }}
        />
      )}

      {/* Face-centering oval hint — only in ready phase (before record) */}
      {phase === "ready" && (
        <div className="absolute inset-0 z-10 pointer-events-none flex flex-col items-center justify-center">
          {/* Subtle dim backdrop to make the guide readable */}
          <div className="absolute inset-0 bg-black/25" />
          {/* Oval outline */}
          <div
            className="relative border-2 border-ivory/60 rounded-full"
            style={{
              width: "min(55vw, 280px)",
              height: "min(70vw, 380px)",
            }}
          />
          <p className="relative mt-4 text-ivory/70 text-sm md:text-base font-medium drop-shadow">
            Place your face here
          </p>
        </div>
      )}

      {/* Preview player */}
      {(phase === "preview" || phase === "uploading") && (
        <video
          ref={previewVideoRef}
          playsInline
          className="absolute inset-0 w-full h-full object-contain bg-black"
        />
      )}

      {/* Teleprompter band at top (40% of viewport) */}
      {link &&
        (phase === "ready" || phase === "countdown" || phase === "recording") && (
          <div className="absolute top-0 left-0 right-0 h-[40vh] bg-gradient-to-b from-black/85 via-black/70 to-transparent text-ivory z-10 pt-6">
            <Teleprompter
              intro={parsed.intro}
              segments={parsed.segments}
              timing={timing}
              elapsedSec={elapsed}
              phase={phase}
            />
          </div>
        )}

      {/* LLYLL-style progress gauge at top — capsule with lemonade fill */}
      {phase === "recording" && (
        <div className="absolute top-4 left-4 right-4 z-20 space-y-2">
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-lemonade transition-[width] duration-100 ease-linear"
              style={{ width: `${Math.min((elapsed / maxSec) * 100, 100)}%` }}
            />
          </div>
          <div className="flex justify-between items-center text-ivory/80">
            <span className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              REC
            </span>
            <span className="font-mono text-xs tabular-nums">
              {Math.max(0, maxSec - elapsed)}s
            </span>
          </div>
        </div>
      )}

      {/* Zoom toggle — single button on the right, available during ready & recording */}
      {(phase === "ready" || phase === "recording") && (
        <button
          onClick={() => setCameraZoom((z) => (z === 1 ? 2 : 1))}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/55 backdrop-blur text-ivory text-sm font-bold flex items-center justify-center shadow-lg active:scale-95"
          aria-label={`Zoom ${cameraZoom === 1 ? "in to 2x" : "out to 1x"}`}
        >
          {cameraZoom}×
        </button>
      )}

      {/* Countdown overlay — LLYLL style: number + coaching hint */}
      {phase === "countdown" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-30 pointer-events-none gap-3">
          <div className="text-ivory text-[180px] font-black leading-none drop-shadow-2xl">
            {countdown}
          </div>
          <div className="text-ivory/90 text-xl font-semibold drop-shadow-lg">
            {countdown === 3 && "Take a deep breath"}
            {countdown === 2 && "Look at the camera"}
            {countdown === 1 && "Smile, you got this!"}
          </div>
        </div>
      )}

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-20 bg-gradient-to-t from-black/80 to-transparent">
        {phase === "ready" && (
          <div className="flex flex-col items-center gap-3">
            {/* LLYLL-style record button: red circle 72px with black inner dot */}
            <button
              onClick={startRecording}
              className="flex items-center justify-center w-[72px] h-[72px] rounded-full bg-red-600 hover:bg-red-700 transition shadow-2xl active:scale-95"
              aria-label="Start recording"
            >
              <span className="w-7 h-7 rounded-full bg-black" />
            </button>
            <p className="text-ivory/70 text-xs">Tap to start</p>
          </div>
        )}

        {phase === "recording" && (
          <button
            onClick={stopRecording}
            className="mx-auto flex items-center justify-center w-[72px] h-[72px] rounded-full bg-red-600 hover:bg-red-700 transition shadow-2xl active:scale-95"
            aria-label="Stop recording"
          >
            <span className="w-7 h-7 bg-black rounded-sm" />
          </button>
        )}

        {phase === "preview" && (
          <div className="flex gap-3 max-w-md mx-auto">
            <button
              onClick={retake}
              className="flex-1 py-4 bg-concrete/80 backdrop-blur text-ivory rounded-xl font-semibold hover:bg-concrete transition border border-stone/30"
            >
              One more
            </button>
            <button
              onClick={submit}
              className="flex-1 py-4 bg-lemonade text-black rounded-xl font-bold hover:brightness-95 transition shadow-xl"
            >
              Send
            </button>
          </div>
        )}

        {phase === "uploading" && (
          <div className="text-center text-ivory max-w-md mx-auto py-4 font-medium">
            Envoi en cours…
          </div>
        )}
      </div>
    </div>
  );
}

function Full({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-charcoal text-ivory flex items-center justify-center p-8">
      {children}
    </div>
  );
}
